/**
 * Gedeelde copy voor project-journey (UI) — geen interne termen naar eindklant.
 */

export type JourneyVariant = 'empty' | 'drawing';

/** Vier tabs — 1:1 met navigatie (geen aparte 3-stappen-iconen meer in zijbalk). */
export const JOURNEY_TAB_STEPS = [
  {
    key: 'gegevens',
    title: 'Gegevens',
    blurb: 'Project, locatie, klanttype en documenten vastleggen.',
  },
  {
    key: 'posten',
    title: 'Posten',
    blurb: 'Begrotingsregels toevoegen en eventueel tekeningvoorstel goedkeuren.',
  },
  {
    key: 'resultaat',
    title: 'Resultaat',
    blurb: 'Totalen en prijsopbouw per regel controleren.',
  },
  {
    key: 'rapportage',
    title: 'Rapportage',
    blurb: 'Offerte genereren en exports downloaden.',
  },
] as const;

export const JOURNEY_STEPS_EMPTY = [
  {
    key: 'dossier',
    title: 'Dossier',
    blurb: 'Naam, projecttype, gebouw, locatie en klantprofiel vastleggen.',
  },
  {
    key: 'posten',
    title: 'Begrotingsposten',
    blurb: 'Metingen koppelen aan het prijsboek (library) op de projectpagina.',
  },
  {
    key: 'totalen',
    title: 'Totalen',
    blurb: 'Kostenopbouw controleren: materialen, arbeid, materieel.',
  },
  {
    key: 'rapportage',
    title: 'Rapportage',
    blurb: 'Offerte genereren en export voor interne of externe besluitvorming.',
  },
] as const;

export const JOURNEY_STEPS_DRAWING = [
  {
    key: 'dossier_docs',
    title: 'Dossier & documenten',
    blurb: 'Gegevens invullen en tekening of PDF uploaden.',
  },
  {
    key: 'analyse',
    title: 'Analyse',
    blurb: 'Systeem leest documenten en maakt een voorstel gekoppeld aan de library.',
  },
  {
    key: 'review_posten',
    title: 'Review & posten',
    blurb: 'Voorstel controleren, goedkeuren en waar nodig begrotingsposten aanscherpen.',
  },
  {
    key: 'totalen_export',
    title: 'Totalen & export',
    blurb: 'Zelfde totalen en rapportage als bij een dossier-start.',
  },
] as const;

/** Contextregel op basis van projecttype × gebouw × klant (één zin, klantgericht). */
export function getJourneyProfileHint(input: {
  projectTypeId: string;
  buildingTypeId: string;
  customerTypeId: string;
  projectTypeLabel: string;
  buildingTypeLabel: string;
  customerTypeLabel: string;
}): string {
  const { customerTypeId, buildingTypeId, projectTypeId, customerTypeLabel, buildingTypeLabel, projectTypeLabel } =
    input;

  const parts: string[] = [];

  // Klantsegment (geen interne termen)
  switch (customerTypeId) {
    case 'particulier':
      parts.push(`${customerTypeLabel}: duidelijke uitleg en overzichtelijke kostenopbouw.`);
      break;
    case 'aannemer':
    case 'adviseur':
      parts.push(`${customerTypeLabel}: snelle detailstructuur per bouwdeel voor calculatie en review.`);
      break;
    case 'projectontwikkelaar':
    case 'vastgoedontwikkelaar':
      parts.push(`${customerTypeLabel}: investeringsraming en vergelijkbare posten tussen faseringen.`);
      break;
    case 'woningcorporatie':
    case 'vve':
      parts.push(`${customerTypeLabel}: transparante posten en herhaalbare uitvraag voor portefeuille of complex.`);
      break;
    case 'huizenflipper':
      parts.push(`${customerTypeLabel}: snelle renovatieposten en rendementsinzicht.`);
      break;
    case 'overheid':
      parts.push(`${customerTypeLabel}: toetsbare onderbouwing en consistente documentatie.`);
      break;
    case 'bank_financier':
      parts.push(`${customerTypeLabel}: gedocumenteerde kostenstructuur voor dossier en risicobeoordeling.`);
      break;
    default:
      parts.push(`${customerTypeLabel}: professionele standaardopzet voor dit dossier.`);
  }

  // Gebouw + traject (kort)
  if (buildingTypeId.includes('utiliteit') || buildingTypeLabel.toLowerCase().includes('util')) {
    parts.push(`Gebouw ${buildingTypeLabel}: nadruk op installaties en programma-eisen.`);
  } else if (buildingTypeId.includes('woning') || buildingTypeLabel.toLowerCase().includes('woning')) {
    parts.push(`Gebouw ${buildingTypeLabel}: passend voor woningtransformaties en verbetering.`);
  } else {
    parts.push(`Gebouw ${buildingTypeLabel}.`);
  }

  if (projectTypeId.includes('nieuwbouw') || projectTypeLabel.toLowerCase().includes('nieuwbouw')) {
    parts.push(`Traject ${projectTypeLabel}: volledige scope-opbouw vanaf ontwerp.`);
  } else if (projectTypeId.includes('renov') || projectTypeLabel.toLowerCase().includes('renov')) {
    parts.push(`Traject ${projectTypeLabel}: focus op bestaand bouwdeel en ingrepen.`);
  } else {
    parts.push(`Traject ${projectTypeLabel}.`);
  }

  return parts.join(' ');
}

/** Tekst voor de stap ná de huidige (currentStep 1–4). */
export function getFollowingStepSummary(variant: JourneyVariant, currentStep1Based: number): string | null {
  const steps = variant === 'empty' ? JOURNEY_STEPS_EMPTY : JOURNEY_STEPS_DRAWING;
  if (currentStep1Based < 1 || currentStep1Based >= steps.length) return null;
  const next = steps[currentStep1Based];
  return `Volgende: ${next.title} — ${next.blurb}`;
}

/** Volgende stap langs de vier dossier-tabs (psychologisch: één duidelijke route). */
export function getFollowingTabStepSummary(currentTabStep1Based: number): string | null {
  const steps = JOURNEY_TAB_STEPS;
  if (currentTabStep1Based < 1 || currentTabStep1Based >= steps.length) return null;
  const next = steps[currentTabStep1Based];
  return `Volgende stap: ${next.title} — ${next.blurb}`;
}
